import React, { useMemo, useState } from "react"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import { toast } from "react-toastify"
import { Link } from "react-router-dom"
import PageWrapper from "../../../components/Common/PageWrapper"
import { CanView } from "../../../components/Common/CanView"
import { ACCESS } from "../../../constants/accessPermissions"
import {
  useNotificationDevices,
  useSendTestNotification,
} from "../../../hooks/useNotificationSettings"

const formatDateTime = value => {
  if (!value) return "N/A"

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value))
}

const deviceLabel = device => {
  const name =
    device.employee_name ||
    device.user_name ||
    device.username ||
    `User #${device.user_id}`
  const platform = device.platform ? ` - ${device.platform}` : ""
  const token = device.fcm_token_preview ? ` - ${device.fcm_token_preview}` : ""

  return `${name}${platform} - FCM${token}`
}

const NotificationSettings = () => {
  document.title = "Notification Settings - Aurobil"

  const [selectedDeviceId, setSelectedDeviceId] = useState("")
  const [lastResult, setLastResult] = useState(null)

  const {
    data: devicesResponse,
    isLoading,
    refetch,
    isRefetching,
  } = useNotificationDevices()
  const { mutateAsync: sendTest, isPending } = useSendTestNotification()

  const devices = useMemo(
    () =>
      devicesResponse && devicesResponse.data && devicesResponse.data.devices
        ? devicesResponse.data.devices
        : [],
    [devicesResponse]
  )

  const selectedDevice = devices.find(
    device => String(device.id) === String(selectedDeviceId)
  )

  const handleSendTest = () => {
    const payload = selectedDeviceId
      ? { device_token_id: Number(selectedDeviceId) }
      : {}

    sendTest(payload)
      .then(response => {
        setLastResult(response && response.data ? response.data : null)
        toast.success(
          response && response.message
            ? response.message
            : "Test notification sent."
        )
      })
      .catch(error => {
        const message =
          error &&
          error.response &&
          error.response.data &&
          error.response.data.message
            ? error.response.data.message
            : "Failed to send test notification."
        toast.error(message)
      })
  }

  return (
    <PageWrapper title="Settings" breadcrumbItem="Notification">
      <Row>
        <Col xl={9}>
          <Card>
            <CardBody>
              <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
                <div>
                  <h4 className="card-title mb-1">Notification</h4>
                  <p className="text-muted mb-0">
                    Test Firebase Cloud Messaging delivery against active mobile
                    devices.
                  </p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <CanView access={[ACCESS.NOTIFICATION_EVENT.MANAGE]}>
                    <Link
                      to="/settings/notification-events"
                      className="btn btn-outline-primary btn-sm"
                    >
                      <i className="mdi mdi-tune me-1" />
                      Manage notification events
                    </Link>
                  </CanView>
                  <Badge color="primary" className="px-3 py-2">
                    {devices.length} Active Device
                    {devices.length === 1 ? "" : "s"}
                  </Badge>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-5">
                  <Spinner size="sm" color="primary" />
                  <p className="text-muted small mt-2">
                    Loading mobile devices...
                  </p>
                </div>
              ) : (
                <>
                  <div className="border rounded p-3 mb-4">
                    <Row className="g-3 align-items-end">
                      <Col md={8}>
                        <Label className="mb-1 small fw-semibold">
                          Mobile Device
                        </Label>
                        <Input
                          type="select"
                          bsSize="sm"
                          value={selectedDeviceId}
                          onChange={event =>
                            setSelectedDeviceId(event.target.value)
                          }
                        >
                          <option value="">My account device</option>
                          {devices.map(device => (
                            <option key={device.id} value={device.id}>
                              {deviceLabel(device)}
                            </option>
                          ))}
                        </Input>
                        <div className="small text-muted mt-1">
                          Choose a connected mobile user/device from your
                          showroom. All test notifications are sent through FCM.
                        </div>
                      </Col>
                      <Col md="auto">
                        <Button
                          color="primary"
                          onClick={handleSendTest}
                          disabled={isPending}
                        >
                          {isPending ? (
                            <>
                              <Spinner size="sm" className="me-1" />
                              Sending
                            </>
                          ) : (
                            <>
                              <i className="mdi mdi-send me-1" />
                              Send Test
                            </>
                          )}
                        </Button>
                      </Col>
                      <Col md="auto">
                        <Button
                          color="secondary"
                          outline
                          onClick={() => refetch()}
                          disabled={isRefetching}
                        >
                          <i className="mdi mdi-refresh me-1" />
                          Refresh
                        </Button>
                      </Col>
                    </Row>
                  </div>

                  {selectedDevice && (
                    <div className="border rounded p-3 mb-4 bg-light">
                      <Row className="g-3">
                        <Col md={4}>
                          <div className="small text-muted">User</div>
                          <div className="fw-semibold">
                            {selectedDevice.employee_name ||
                              selectedDevice.user_name ||
                              selectedDevice.username}
                          </div>
                        </Col>
                        <Col md={2}>
                          <div className="small text-muted">Platform</div>
                          <div className="fw-semibold text-capitalize">
                            {selectedDevice.platform || "N/A"}
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="small text-muted">Push Path</div>
                          <div>
                            <Badge color="success">FCM</Badge>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div className="small text-muted">Last Seen</div>
                          <div className="fw-semibold">
                            {formatDateTime(selectedDevice.last_used_at)}
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="small text-muted">Token Preview</div>
                          <div className="small">
                            FCM:{" "}
                            <code>
                              {selectedDevice.fcm_token_preview || "N/A"}
                            </code>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}

                  {devices.length === 0 && (
                    <Alert color="warning">
                      No mobile devices are registered yet. Log in from the Expo
                      development build and allow notifications, then refresh
                      this page.
                    </Alert>
                  )}

                  {lastResult && (
                    <Alert color="success" className="mb-0">
                      Sent to {lastResult.token_count} mobile device
                      {lastResult.token_count === 1 ? "" : "s"} through FCM
                      {lastResult.fcm_configured
                        ? ""
                        : " (backend credentials not configured)"}
                      .
                    </Alert>
                  )}
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </PageWrapper>
  )
}

export default NotificationSettings
